import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, GITES, HERO_IMG } from "@/data";
import { GroupOffer } from "@/components/GroupOffer";
import { FamilyStory } from "@/components/FamilyStory";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" } }),
};

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section data-testid="home-hero" className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="La terrasse avec vue sur la vallée" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest-dark/85 via-forest-dark/45 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full py-28">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-2xl">
            <h1 className="font-serif font-light text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Deux gîtes.<br />Un autre point de vue<br />sur la Bourgogne.
            </h1>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/les-maisons">
                <Button data-testid="hero-cta-maisons" className="rounded-full bg-cream hover:bg-white text-forest px-8 h-12 text-base">
                  Découvrir les gîtes <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href={BRAND.emailLink} data-testid="hero-cta-ecrire">
                <Button variant="outline" className="rounded-full border-cream/60 text-cream bg-white/10 hover:bg-cream hover:text-forest px-8 h-12 text-base">
                  <Mail className="mr-2 w-4 h-4" /> Écrire à Anne
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Éditorial */}
      <section data-testid="home-editorial" className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">Le Point de Vue de Tanlay</span>
            <h2 className="font-serif text-4xl lg:text-5xl font-light mt-5 leading-tight text-forest">
              Une adresse pour habiter pleinement la Bourgogne, le temps d'un séjour.
            </h2>
            <p className="text-charcoal/70 text-lg leading-relaxed mt-8">
              Ici, le paysage donne le tempo. Le matin commence sur la terrasse, la journée se poursuit le long du canal ou dans les vignes, et le soir ramène à la chaleur d'un gîte choisi pour soi.
            </p>
            <p className="text-charcoal/60 mt-6 max-w-xl">{BRAND.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Les deux maisons */}
      <section data-testid="home-maisons" className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-8">
          {GITES.map((g, i) => (
            <motion.div key={g.id} data-testid={`home-maison-${g.id}`} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <Link to={g.path} className="group block">
                <div className="overflow-hidden rounded-2xl">
                  <img src={g.cardImg || g.cover} alt={g.name} className="w-full h-[380px] object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <h3 className="font-serif text-3xl text-forest mt-6">{g.name}</h3>
                <p className="text-charcoal/70 mt-2 leading-relaxed">{g.tagline}</p>
                <span className="inline-flex items-center gap-2 mt-4 text-forest text-sm font-medium group-hover:gap-3 transition-all">
                  Voir le gîte <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                <a href={g.booking} target="_blank" rel="noopener noreferrer" data-testid={`home-booking-${g.id}`}>
                  <Button className="rounded-full bg-forest hover:bg-forest-dark text-cream px-6">
                    Réserver {g.name} sur Booking <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <a href={g.booking} target="_blank" rel="noopener noreferrer" data-testid={`home-reviews-${g.id}`} className="text-sm text-forest underline underline-offset-4 hover:text-forest-dark">
                  Lire les avis sur Booking →
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <FamilyStory locale="fr" />

      <GroupOffer />
    </div>
  );
}
